import { useCallback, useMemo, useState } from 'react';

import { useModalState } from '../../../../hooks/index';
import {
  collectFormFileNames,
  collectResolvedStorageFileNames,
  FileToRename,
  FileWithDisplayName,
  findFirstDuplicateInDrop,
  getFileDisplayName,
  getResolvedFileDisplayName,
  hasDuplicateNamesInForm,
  resolveStorageFileName,
} from '../../../../utils/file-upload-duplicate-names.util';

type UseEventFileUploadDuplicatesParams = {
  files: FileWithDisplayName[];
  onRenameInForm: (params: { currentName: string; newName: string }) => void;
};

export const useEventFileUploadDuplicates = ({
  files,
  onRenameInForm,
}: UseEventFileUploadDuplicatesParams) => {
  const [filesToRename, setFilesToRename] = useState<FileToRename[]>([]);

  const addFileToRename = useCallback((link: string, name: string, oldName: string) => {
    setFilesToRename((prev) => [...prev, { link, name, oldName }]);
  }, []);

  const removeFileToRename = useCallback((nameToRemove: string | undefined) => {
    setFilesToRename((prev) => prev.filter((file) => file.oldName !== nameToRemove));
  }, []);

  const hasUnresolvedDuplicateNames = hasDuplicateNamesInForm(files, filesToRename);

  const renameModalFolderNames = useMemo(
    () => collectResolvedStorageFileNames(files, filesToRename).map((name) => name.toLowerCase()),
    [files, filesToRename],
  );

  const isFileNameDuplicate = useCallback(
    (doc?: { name?: string }, docLink?: string) => {
      const name = getResolvedFileDisplayName(getFileDisplayName(doc), filesToRename);
      if (!name) return false;

      const storageName = resolveStorageFileName(name, docLink);
      const resolvedStorageNames = collectResolvedStorageFileNames(files, filesToRename);
      return resolvedStorageNames.filter((resolvedName) => resolvedName === storageName).length > 1;
    },
    [files, filesToRename],
  );

  const validateDropFiles = useCallback(
    (acceptedFiles: File[], existingFiles: FileWithDisplayName[]) =>
      findFirstDuplicateInDrop(acceptedFiles, collectFormFileNames(existingFiles)),
    [],
  );

  const resolveDisplayName = useCallback(
    (doc?: { name?: string }) => getResolvedFileDisplayName(getFileDisplayName(doc), filesToRename),
    [filesToRename],
  );

  const {
    toggler: renameModalToggler,
    isOpen: isOpenRenameModal,
    handleOpenModal: handleOpenRenameModal,
    handleCloseModal: handleCloseRenameModal,
    handleSuccessModal: handleSuccessRenameModal,
    modalData: currentName,
  } = useModalState<string | undefined, unknown, string>({
    onSuccess: (name?: string) => {
      if (!name || !currentName) return;

      const currentFile = files.find((file) => getFileDisplayName(file.doc) === currentName);
      if (currentFile?.docLink) {
        addFileToRename(currentFile.docLink, name, currentName);
        return;
      }

      onRenameInForm({ currentName, newName: name });
    },
  });

  return {
    filesToRename,
    hasUnresolvedDuplicateNames,
    renameModalFolderNames,
    isFileNameDuplicate,
    validateDropFiles,
    removeFileToRename,
    resolveDisplayName,
    renameModal: {
      currentName,
      handleCloseRenameModal,
      handleOpenRenameModal,
      handleSuccessRenameModal,
      isOpenRenameModal,
      renameModalToggler,
    },
  };
};
